import { BehaviorSubject, Observable, Subscription, TimeoutError } from 'rxjs'
import { timeout } from 'rxjs/operators'
import { StandOperationResult } from '@/ble/entities/standOperationResult'
import { OperationStatus, readTimesOf } from '@/ble/entities/operationStatus'
import { StandProtocol } from '@/ble/entities/standProtocol'
import { FirmwareCapabilities } from '@/ble/entities/firmwareCapabilities'
import { TagId } from '@/ble/entities/tagId'
import { SwitchStatus } from '@/ble/entities/switchStatus'
import {
  StandOperationState,
  IDLE_OPERATION_STATE,
  stageAdvances,
} from '@/ble/entities/standOperationState'
import {
  OPERATION_TIMEOUT_MS,
  BLE_STATE_POST_TIMEOUT_MS,
  RESULT_TIMEOUT_SCHEDULE_GRACE_MS,
} from '@/ble/infrastructures/bleConfig'
import { WriteFailedException } from '@/ble/errors/writeFailed'
import { ResultWaitTimeoutException } from '@/ble/errors/resultWaitTimeout'
import { TransportTimeoutException } from '@/ble/errors/transportTimeout'
import {
  BleStateContext,
  BleStatePayload,
  BleStateResultPayload,
  BleStateForcedClosedPayload,
  buildWrotePayload,
  buildResultPayload,
  buildForcedClosedPayload,
} from './bleStateReporter'

// usecase が repository から消費する最小の口。concrete StandOperationRepository が満たす。
export interface StandOperationPort {
  readonly protocol: StandProtocol
  readonly capabilities: FirmwareCapabilities
  readonly observeOperationResult$: Observable<StandOperationResult>
  // 接続以降の notify 受信回数の単調カウンタ。電池応答(AES 0x08)や
  // malformed drop も含め全frameを数える。write前後のスナップショット差分で
  // 「write後に何か届いたか」を判定する（sawAnyFrame の材料）。
  readonly notifyFrameCount: number
  writeTakeOut(): Promise<void>
  writePutIn(): Promise<void>
  readStandBattery(): Promise<number>
  disconnect(): Promise<void>
  probeLinkAlive(): Promise<boolean> // リンク生存確認(#7709)。成功のみ true・throw しない契約
}

// /ble/state POST の seam。TODO(aikasa/ikasa_platform#7595): 実 HTTP client(useRequest silent)の注入。本Taskではテスト/デバッグ用の fake を注入する。
export type PostBleState = (payload: BleStatePayload) => Promise<void>

// 画面境界に返す操作結果。
export type StandOperationOutcomeResult = {
  outcome: OperationStatus
  readTimes: number
  tagId: TagId | null // selectedTagId
  standBattery: number
  protocol: StandProtocol
  // 振り分け純関数（decideOperationDispatch）の入力
  switchStatus: SwitchStatus
  area1TagId: TagId | null
  area2TagId: TagId | null
  forcedUnlock: boolean
}

// takeOut/putIn の完了 union。frame はハード応答由来の結果、manualCompleted は
// ユーザー申告(手動完了ボタン)由来の結果。ハード応答ではないため OperationStatus
// には足さず、ここで型を分ける。
export type StandServiceOutcome =
  | { kind: 'frame'; result: StandOperationOutcomeResult }
  | { kind: 'manualCompleted' }

// 呼び出し側(aikasa/ikasa_platform#7595 の hook)が用意する当事者情報。when は takeOut/putIn が決める。
export type StandOperationContext = {
  ikasaId: string
  standId: string
  processId: string
  appVersion: string
}

const ERROR_BATTERY_VALUE = 999 // platform errorBatteryValue。V14 telemetry 欠落時の防御値。

// 1インスタンス=1操作。claimed/recordQueue/lastCompletionRecordState 等の完了状態は
// リセットしないため、takeOut/putIn を同一インスタンスで再実行してはならない
// (hook 側が execute 毎に new する前提)。
export class StandService {
  private readonly operationTimeoutMs: number
  private readonly postTimeoutMs: number
  private readonly now: () => number
  // 操作進行状態。hook がミラーして UnlockPage のボタン表示可否を導出する。
  private readonly operationStateSubject =
    new BehaviorSubject<StandOperationState>(IDLE_OPERATION_STATE)

  constructor(
    private readonly repository: StandOperationPort,
    private readonly postBleState: PostBleState,
    options?: {
      operationTimeoutMs?: number
      postTimeoutMs?: number
      now?: () => number // テストからの時計注入用(既定 Date.now)。
    }
  ) {
    this.operationTimeoutMs =
      options?.operationTimeoutMs ?? OPERATION_TIMEOUT_MS
    this.postTimeoutMs = options?.postTimeoutMs ?? BLE_STATE_POST_TIMEOUT_MS
    this.now = options?.now ?? Date.now
  }

  get observeOperationState$(): Observable<StandOperationState> {
    return this.operationStateSubject.asObservable()
  }

  private emitStage(
    stage: StandOperationState['stage'],
    offerManualCompletion: boolean
  ): void {
    // stage は前進のみ(単調)。各イベントは自分の発生時に遷移を試みるが、到着順が
    // 入れ替わっても状態を巻き戻さない——final frame が write ack より先に届いて
    // claim(settled)した後、遅れて解決した ack が wrote を再 emit して閉じた手動完了
    // ボタンを再表示する、という順序依存の巻き戻しをここで構造的に断つ。
    if (!stageAdvances(this.operationStateSubject.value.stage, stage)) return
    this.operationStateSubject.next({ stage, offerManualCompletion })
  }

  // 完了確定の単一関門(one-shot claim)。全完了経路(フレーム/手動/timeout/エラー)が
  // ここを通ってから settle する。先勝ち。敗者は取引に進まない。
  // JS は単一スレッドなので boolean で足りる(platform ShareCompletionClaim の Completer と等価)。
  private claimed = false
  // write の ack 受領時刻。結果待ち窓の起点(コマンド配達)が定刻だったかの判定材料。
  private writeAckAt: number | null = null
  private tryClaim(offerManualCompletion: boolean): boolean {
    if (this.claimed) return false
    this.claimed = true
    this.emitStage('settled', offerManualCompletion) // claim 成立＝窓が閉じる
    return true
  }

  // 実行中操作の手動完了受け口。runFlow 開始時にセットし finally で解除。
  private activeOperation: {
    bleCtx: BleStateContext
    resolveManual: () => void
  } | null = null

  // 手動完了(ユーザー申告)。claim 敗者は記録も含め完全 no-op
  // (native の manualComplete 先頭の tryClaim 準拠)。
  manualComplete(): void {
    const op = this.activeOperation
    if (op === null) return
    if (!op.bleCtx.forcedUnlock) return // 後開錠はボタン自体出ないが機構でも拒否する
    // 窓の開始(stage='wrote')を機構側でも強制する。UI 側の表示条件は契約であって機構ではない。
    // wrote の意味は protocol で異なる: AES(with-response)=ATT層の受領後 / V14(without-response)=発行時点。
    // V14 で書込未達のままタップされる露出は ack timeout(3s) で有界（aikasa/ikasa_platform#7705）。
    if (this.operationStateSubject.value.stage !== 'wrote') return
    // ここでの tryClaim の本質は claimed を立てて frame 経路を封じること。敗者弾き自体は
    // 上の stage/activeOperation ガードが先に担う(claim 済みなら stage は settled になっている
    // ため false 枝には実質到達しない)が、単一関門の不変条件として明示的に通す。
    if (!this.tryClaim(op.bleCtx.forcedUnlock)) return
    // 記録(有界) → 取引へ(resolve)、の順で確定させる。
    void this.postCompletionRecord(buildForcedClosedPayload(op.bleCtx)).then(
      () => op.resolveManual()
    )
  }

  // 完了記録の直列化。単一の promise 連鎖に流すだけの JS の慣用ミューテックスで、
  // 排他・発行順(FIFO)・closed↔forced_closed の先勝ち dedup の3不変条件を構造で保証する
  // (platform は同じ排他を synchronized パッケージの Lock 1行に任せている。守る対象は
  // 不変条件であってロック実装の内部挙動ではない)。pre_closed は dedup 対象外。
  // platform は成功時のみ prev を更新するが、spot の safePost は失敗を握り潰すため
  // 「試行した」時点で確定させる(失敗時に相方が後追い送信されない差は回収網が拾う)。
  private lastCompletionRecordState: 'closed' | 'forced_closed' | null = null
  private recordQueue: Promise<void> = Promise.resolve()
  private postCompletionRecord(
    payload: BleStateResultPayload | BleStateForcedClosedPayload
  ): Promise<void> {
    const run = async () => {
      const prev = this.lastCompletionRecordState
      if (
        (payload.state === 'closed' && prev === 'forced_closed') ||
        (payload.state === 'forced_closed' && prev === 'closed')
      ) {
        console.warn('[ble] completion record deduped', {
          state: payload.state,
          prev,
        })
        return
      }
      if (payload.state === 'closed' || payload.state === 'forced_closed') {
        this.lastCompletionRecordState = payload.state
      }
      await this.safePost(payload)
    }
    this.recordQueue = this.recordQueue.then(run)
    return this.recordQueue
  }

  takeOut(ctx: StandOperationContext): Promise<StandServiceOutcome> {
    return this.runFlow(ctx, 'rental', () => this.repository.writeTakeOut())
  }

  putIn(ctx: StandOperationContext): Promise<StandServiceOutcome> {
    return this.runFlow(ctx, 'return', () => this.repository.writePutIn())
  }

  private async runFlow(
    ctx: StandOperationContext,
    when: 'rental' | 'return',
    write: () => Promise<void>
  ): Promise<StandServiceOutcome> {
    const protocol = this.repository.protocol // 未接続なら throw(事前条件)
    const forcedUnlock = this.repository.capabilities.canDistinguishReaders
    const bleCtx: BleStateContext = { ...ctx, when, forcedUnlock }
    let wrotePosted = false
    // write後にnotifyが1件でも届いたかを判定するためのスナップショット。
    // catch節(toTerminalError)から参照するためtry外で宣言する。
    let framesBeforeWrite = 0
    // 結果待ち開始時刻。framesBeforeWrite と同じ箇所でスナップショットする。
    let waitStartedAt = 0
    try {
      // AES は電池を write の前に 0x08 で読む(現行spotと同順序・0x08を挟んでもshare音が鳴ることは実機確認済み)。V14 は結果frame由来。
      const aesBattery =
        protocol === 'aes' ? await this.repository.readStandBattery() : null

      await this.safePost(buildWrotePayload(bleCtx))
      wrotePosted = true
      this.emitStage('writing', forcedUnlock) // write 直前

      // 手動完了とフレーム完了の合流点。どちらが先に claim を通っても、その側だけが settle させる。
      let resolveManual!: () => void
      const manualCompleted = new Promise<StandServiceOutcome>((resolve) => {
        resolveManual = () => resolve({ kind: 'manualCompleted' })
      })
      this.activeOperation = { bleCtx, resolveManual }

      // wrote POST 完了後・observeFinalResult(write発行)の直前でスナップショットする。
      // ここまでの受信(AES電池応答等)はframesBeforeWriteに含まれ、以後の増分のみが
      // 「write後の受信」としてsawAnyFrameの判定材料になる。
      framesBeforeWrite = this.repository.notifyFrameCount
      waitStartedAt = this.now()
      const frameResult = this.observeFinalResult(bleCtx, write).then(
        (result): StandServiceOutcome => ({
          kind: 'frame',
          result: {
            outcome: result.outcome,
            readTimes: readTimesOf(result.outcome),
            tagId: result.selectedTagId,
            standBattery:
              protocol === 'aes'
                ? (aesBattery as number)
                : result.telemetry?.batteryLevel ?? ERROR_BATTERY_VALUE,
            protocol,
            switchStatus: result.switchStatus,
            area1TagId: result.area1TagId,
            area2TagId: result.area2TagId,
            forcedUnlock,
          },
        })
      )
      // manual 側が先に claim して勝った場合、frameResult は claim guard で reject されず
      // 未settleのまま残るか稀に reject されうる。いずれにせよ race の敗者として
      // unhandled rejection を出さないための先行 catch。
      frameResult.catch(() => undefined)
      return await Promise.race([frameResult, manualCompleted])
    } catch (e) {
      // 結果待ち系の終端を typed exception へ正規化する(#7709)。probe は finally の
      // 切断発行より必ず先に走る(catch→finally の構文順序)。wrote POST 前の失敗は
      // 台帳行が存在しないため正規化せず素通しする(閉じる対象がない)。
      if (!wrotePosted) throw e
      const {
        error: terminalError,
        elapsedMs,
        ackDelayMs,
      } = await this.toTerminalError(
        e,
        forcedUnlock,
        framesBeforeWrite,
        waitStartedAt
      )
      // 終端 breadcrumb。タイムアウト正規化時のみ(write失敗等の
      // 素通し経路には出さない)。probe 実行有無に依らず記録する。
      if (terminalError instanceof ResultWaitTimeoutException) {
        console.info('[ble] result-wait terminal', {
          processId: bleCtx.processId,
          forcedUnlock: terminalError.forcedUnlock,
          linkAlive: terminalError.linkAlive,
          sawAnyFrame: terminalError.sawAnyFrame,
          timedOutOnSchedule: terminalError.timedOutOnSchedule,
          // timedOutOnSchedule=false のとき suspend(実経過超過)か ack 遅延かを切り分けるための実測値
          // (probe往復を挟む前に発火直後で計測済みの値をそのまま流用)。
          elapsedMs,
          ackDelayMs: Number.isFinite(ackDelayMs) ? ackDelayMs : 'no-ack',
        })
      }
      throw terminalError
    } finally {
      this.activeOperation = null
      // settled emit は claim 成立時(tryClaim内)で実施済み。未 claim のまま finally に
      // 到達する異常経路(想定外の早期throw等)のみ、ここでフォールバックとして emit する。
      if (!this.claimed) this.emitStage('settled', forcedUnlock)
      // 成功/irregular/エラーいずれも操作後に切断(現行spotの close-after-result)。
      // 後始末は結果配達をブロックしない(aikasa/ikasa_platform#7706): 従来はここで await して
      // おり、死んだ機器への close 無応答が結果(エラー)を画面に届かなくする hang の原因だった。
      // disconnect は infra 層の cap で有界に settle する(#7706)が、完了を待つとエラー表示が
      // cap 分(最大数秒)遅れるため、発行だけして裏で完了させる。「次の open が pending の
      // close を追い越さない」順序は、adapter close の同期キュー予約(SpjsStandPeripheral.disconnect
      // が closeAdapter を呼び出しと同期で発行する・#7706)＋ central の adapterSerializer
      // (発行順=完了順)が JS 側の発行順として保証する(cap で放棄した呼び出しの native 側
      // 完了順の限界は closeAdapterAfter のコメント参照)。
      // 切断失敗で操作結果は殺さないが、事実は breadcrumb として残す(実機デバッグ用)。
      void this.repository
        .disconnect()
        .catch((e) =>
          console.warn('[ble] post-operation disconnect failed (ignored)', e)
        )
    }
  }

  // 結果待ち終端の正規化(#7709)。timeout 以外(WriteFailedException・stream error 等)は素通し。
  // elapsedMs/ackDelayMs は breadcrumb 専用の副産物(例外自体のフィールドは変更しない)で、
  // probe 往復を挟む前・発火直後の計測値をそのまま呼び出し元へ持ち帰る。
  private async toTerminalError(
    e: unknown,
    forcedUnlock: boolean,
    framesBeforeWrite: number,
    waitStartedAt: number
  ): Promise<{ error: unknown; elapsedMs: number; ackDelayMs: number }> {
    // 定刻発火チェックの実経過(#7709)。タイマが実経過で見て定刻(operationTimeoutMs)±猶予を
    // 超えて発火した場合、その間ランタイムが suspend されていた(=フレーム喪失の可能性)と
    // みなし、沈黙の証拠を無効化する。probe 往復の時間を混ぜないよう発火直後に計測する。
    const elapsedMs = this.now() - waitStartedAt
    // 窓の起点(=コマンド配達。write ack)も定刻であることを要求する。ack がブリッジ側で
    // 大幅遅延した場合、20秒窓がスタンドの動作窓(約7秒)を包含しない可能性があるため
    // 「観測窓は健全」と言えない。ack 未受領のままここへ来る経路は防御的に不健全扱い。
    const ackDelayMs =
      this.writeAckAt === null ? Infinity : this.writeAckAt - waitStartedAt
    if (e instanceof TransportTimeoutException) {
      // V14 の ack/result timeout。V14 は常に先開錠なので probe 不要(閉じない判定が確定)。
      // sawAnyFrame も同様に先開錠では不使用(null)。timedOutOnSchedule も同様(先開錠は
      // 閉じないためこの判定自体を使わない)。
      return {
        error: new ResultWaitTimeoutException({
          forcedUnlock,
          linkAlive: null,
          sawAnyFrame: null,
          timedOutOnSchedule: null,
          originalError: e,
        }),
        elapsedMs,
        ackDelayMs,
      }
    }
    const isRxTimeout =
      e instanceof TimeoutError ||
      (e as { name?: string } | null)?.name === 'TimeoutError'
    if (isRxTimeout) {
      // timeout({each})は中間フレームでリセットされるため実経過が定刻を超えることが
      // あるが、その場合は sawAnyFrame=true が確定しており閉じる判定に到達しない。
      const timedOutOnSchedule =
        elapsedMs <=
          this.operationTimeoutMs + RESULT_TIMEOUT_SCHEDULE_GRACE_MS &&
        ackDelayMs <= RESULT_TIMEOUT_SCHEDULE_GRACE_MS
      // 「沈黙」はバイト列が1件も届かなかったことを指す。malformed drop・中間読取
      // (pre_closed)を含め、write後に何か届いていれば結果不明として扱う。
      const sawFramesBeforeProbe =
        this.repository.notifyFrameCount > framesBeforeWrite
      // AES 20s backstop。後開錠のみ probe で「沈黙中リンクが生きていたか」を検証する。
      const linkAlive = forcedUnlock ? null : await this.probeLinkAliveSafe()
      // probe 往復中に届いたフレームも反証として拾う単調OR。probe 中に peripheral が
      // null化されカウンタが0に戻っても、probe前に確定した観測は失われない。
      const sawAnyFrame =
        sawFramesBeforeProbe ||
        this.repository.notifyFrameCount > framesBeforeWrite
      return {
        error: new ResultWaitTimeoutException({
          forcedUnlock,
          linkAlive,
          sawAnyFrame,
          timedOutOnSchedule,
          originalError: e,
        }),
        elapsedMs,
        ackDelayMs,
      }
    }
    return { error: e, elapsedMs, ackDelayMs }
  }

  // StandOperationPort.probeLinkAlive は「成功のみ true・throw しない」契約(interface コメント
  // 参照)。実体の StandOperationRepository.probeLinkAlive は自前で catch して必ず boolean を
  // 返すため、この catch は同じ失敗の二重ログではなく、契約に反して throw した port 実装
  // (契約違反)だけを拾う防御深度。メッセージを区別して両者を混同しない。
  private async probeLinkAliveSafe(): Promise<boolean> {
    try {
      return await this.repository.probeLinkAlive()
    } catch (err) {
      console.warn(
        '[ble] link probe port contract violation (threw instead of resolving false; treated as link-dead)',
        err
      )
      return false
    }
  }

  // 購読を write の前に張り(hot stream の初回emit取りこぼし防止)。final frame(非中間・
  // 非irregular)は受信した瞬間に同期的に claim し(claim〜到着の間に await を挟まない)、
  // 完了記録の POST は claim の後に直列実行する。POST を挟んでから claim すると、POST
  // 待ちの間(最大 postTimeoutMs)に後着のタップが先に claim を取り、実タグを持つ正常
  // フレームが手動取引に化けてしまう窓ができるため。
  // timeout({each})が AES の hang(沈黙/preClosed後沈黙)を防ぐ。
  private observeFinalResult(
    bleCtx: BleStateContext,
    write: () => Promise<void>
  ): Promise<StandOperationResult> {
    const result$ = this.repository.observeOperationResult$
    return new Promise<StandOperationResult>((resolve, reject) => {
      // 実repo(StandOperationRepository.writeWithAnalytics)は write 失敗時に
      // analytics→disconnect()の順で後始末し、そのdisconnect()内部の
      // clearOperationState()がconnectionSubをunsubscribeする副作用として
      // result$を「同期的に」completeさせる。これは write() が返した Promise 自体が
      // reject として観測可能になる（.catch/.thenが呼ばれる）より前に起こるため、
      // 何も対策しないと complete ハンドラが即座に汎用エラーで reject し、
      // 本来の write エラーを握り潰してしまう。
      // 対策: complete 到達時点で write の確定(成否)がまだ分からなければ、
      // write が確定するまで reject 内容の決定を遅延する。write が失敗していたら
      // その write エラーで reject し、write が成功していた(のに結果が来ないまま
      // completeした)場合のみ汎用エラーで reject する。
      // ただし write が永遠に確定しない経路（writeWithAnalytics の内部disconnect()で
      // spjs close がhangすると `throw e` に到達せずwriteがpendingのまま）があるため、
      // 遅延は operationTimeoutMs で有界化する: fallbackタイマが発火したら write の
      // 確定を待つのを諦めて汎用complete-errorで reject する（無限サスペンド禁止）。
      let writeOutcome: { ok: true } | { ok: false; error: unknown } | undefined
      let onWriteOutcome:
        | ((o: NonNullable<typeof writeOutcome>) => void)
        | undefined
      let fallbackTimer: ReturnType<typeof setTimeout> | undefined
      let resolved = false // final frame の勝者として resolve 済みか（complete ハンドラの分岐に使う）

      const sub: Subscription = result$
        .pipe(timeout({ each: this.operationTimeoutMs }))
        .subscribe({
          next: (r) => {
            if (r.outcome === 'irregular') {
              // 逸脱: irregular は解析不能なので closed を POST せず、console に breadcrumb。
              // 「wrote あり closed なし」で未処理プロセスが残り、既存の来訪時確認が回収する。
              console.warn('[ble] irregular response (not posted)', {
                rawResponseHex: r.rawResponseHex.join(''),
              })
              if (!bleCtx.forcedUnlock) {
                // 後開錠 legacy はボタンが無く待たせる意味がないため従来どおり即 fold。
                if (!this.tryClaim(bleCtx.forcedUnlock)) return
                resolved = true
                resolve(r)
              }
              // 先開錠(forcedUnlock)は fold もせずボタン窓(stage=wrote)を維持する。
              // 後続の完了経路はユーザーのタップ(manualComplete)か operationTimeoutMs。
              return
            }
            if (r.isIntermediateRead) {
              // 中間結果(pre_closed)は記録のみ。claim はせず stage も進めない(ボタン窓維持)。
              void this.postCompletionRecord(buildResultPayload(bleCtx, r))
              return
            }
            // final frame: 受信した瞬間に claim してボタン窓を閉じる(await を挟まない)。
            if (!this.tryClaim(bleCtx.forcedUnlock)) {
              // 敗者(手動完了が先に claim 済み)は記録のみ。closed↔forced_closed の
              // 衝突は記録キューの dedup が先勝ちで畳む。
              void this.postCompletionRecord(buildResultPayload(bleCtx, r))
              return
            }
            // 勝者: 記録(直列・有界)を取引より先に完了させてから resolve する
            // (記録→取引の順序は維持。POST 遅延は正しさでなくレイテンシにだけ効く)。
            resolved = true
            void this.postCompletionRecord(buildResultPayload(bleCtx, r)).then(
              () => resolve(r)
            )
          },
          error: (e) => {
            // timeout/stream error の敗者(手動完了が先に claim 済み)は reject しない。
            if (!this.tryClaim(bleCtx.forcedUnlock)) return
            reject(e)
          },
          complete: () => {
            // final frame の勝者として resolve 済みなら deferred settle は不要。
            if (resolved) return
            const settle = (o: NonNullable<typeof writeOutcome>) => {
              // cancel/disconnect 由来の complete が手動完了勝ち後に来ても reject しない。
              if (!this.tryClaim(bleCtx.forcedUnlock)) return
              reject(
                o.ok
                  ? new Error('operation stream completed before final result')
                  : new WriteFailedException(o.error)
              )
            }
            if (writeOutcome) {
              settle(writeOutcome)
            } else {
              onWriteOutcome = settle
              // write が確定しないままでも有界に reject する fallback（上記コメント参照）。
              fallbackTimer = setTimeout(() => {
                onWriteOutcome = undefined // 以後 write が確定しても二重 settle しない
                settle({ ok: true }) // write エラーは得られなかった → 汎用complete-error
              }, this.operationTimeoutMs)
            }
          },
        })
      // subscribe 後に write(取りこぼし防止)。write 失敗は購読を teardown して即 reject。
      write().then(
        () => {
          // 手動完了ボタンの窓は write() resolve 後に開く。resolve の意味は protocol で異なる:
          // AES(with-response)=ATT層の受領後 / V14(without-response)=発行時点
          // （native の withoutResponse とパリティ・aikasa/ikasa_platform#7705）。
          // V14 で書込が実際に届いていない場合も窓は開くが、ack timeout(3s) の
          // stream error → claim が窓を閉じるため露出は有界。
          this.writeAckAt = this.now()
          this.emitStage('wrote', bleCtx.forcedUnlock)
          writeOutcome = { ok: true }
          if (fallbackTimer !== undefined) clearTimeout(fallbackTimer)
          onWriteOutcome?.(writeOutcome)
        },
        (e) => {
          writeOutcome = { ok: false, error: e }
          if (fallbackTimer !== undefined) clearTimeout(fallbackTimer)
          onWriteOutcome?.(writeOutcome)
          sub.unsubscribe()
          // この reject は claim を通さない唯一の settle: stage が writing のまま（wrote 未達）のため manualComplete と競合し得ない。
          // ※AES(with-response) 専用の経路。V14 は writeV14 が発行時 resolve するためここへ来ず、
          //   失敗は ack timeout(3s)→result stream error が受け皿（aikasa/ikasa_platform#7705）。
          reject(new WriteFailedException(e))
        }
      )
    })
  }

  // /ble/state POST は失敗しても操作を殺さない(platform recordBleState の catchError 準拠)。
  // さらに settle しない POST も postTimeoutMs で打ち切って操作を継続する。wrote POST は
  // write 前の await・結果 POST は concatMap 内の await なので、注入 client(素の fetch で
  // timeout 無し)が固まると操作全体が無期限ハングするため、ここで有界化する。
  private async safePost(payload: BleStatePayload): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const post = this.postBleState(payload)
      // bound 超過後に post が遅れて reject しても unhandled rejection にしない先行 catch。
      // (race で先に確定した場合はこの分岐が余分に飲むだけで挙動は変わらない)
      post.catch(() => undefined)
      const raced = await Promise.race([
        post,
        new Promise<'timeout'>((resolveTimeout) => {
          timer = setTimeout(
            () => resolveTimeout('timeout'),
            this.postTimeoutMs
          )
        }),
      ])
      if (raced === 'timeout') {
        console.warn('[ble] /ble/state post timed out (continuing)', {
          state: payload.state,
        })
      }
    } catch (e) {
      console.warn('[ble] /ble/state post failed (swallowed)', e)
    } finally {
      // post が先に settle した場合もタイマを残さない(テストのタイマ残留防止含む)。
      if (timer !== undefined) clearTimeout(timer)
    }
  }
}
